import SEO from "@/components/SEO";
import { Reveal, Eyebrow, PageBand, CTABand, PractitionerCard, GoldRule } from "@/components/Shared";
import { TEAM } from "@/data/content";
import { PAGE_HEROES } from "@/data/photos";
import { FileText, ShieldCheck, Stethoscope, BadgeCheck } from "lucide-react";

const About = () => (
  <>
    <SEO title="The Practice | Lumière Aesthetics, Cottesloe" description="Why Lumière exists: an honest opinion before a treatment, from someone qualified to give it. Doctor-run, inside an established Cottesloe medical centre." />
    <PageBand
      eyebrow="The Practice"
      title="A different kind of aesthetics practice"
      standfirst="Built around a conversation, not a transaction."
      image={PAGE_HEROES.about}
    />

    <section className="bg-warm-white" data-testid="about-why">
      <div className="max-w-[68ch] mx-auto px-8 py-24 lg:py-28">
        <Reveal>
          <Eyebrow className="mb-4">Why we exist</Eyebrow>
          <div className="text-ink/85 space-y-6 leading-[1.8] text-[17px]">
            <p>
              Lumière was founded on a simple observation: most people considering an aesthetic treatment want an
              honest opinion before they want a treatment. They want to know what will actually help, what won't, and
              what it will cost — from someone qualified to tell them.
            </p>
            <p>
              So we built a practice around that conversation. A doctor assesses you before any medical treatment.
              Every treatment plan is made and overseen by a doctor. A doctor on site every treatment day — because we
              think that is simply what a medical practice should look like.
            </p>
            <p className="text-soft-grey text-sm" data-testid="about-doctor-run-note">
              Skin, laser and device treatments are delivered by our registered nurse under doctor-written protocols.
              Each practitioner's profile shows the treatments they provide.
            </p>
            <p>
              We are deliberately conservative. Natural results age well; overdone results don't. If we think a
              treatment isn't right for you, we'll say so — and that is a normal outcome of a consultation here, not
              a failure of one.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-cream" data-testid="about-setting">
      <div className="max-w-[68ch] mx-auto px-8 py-24">
        <Reveal>
          <Eyebrow className="mb-4">The setting</Eyebrow>
          <p className="text-ink/85 leading-[1.8] text-[17px]">
            We practise from an established medical centre on Marine Parade, Cottesloe, two minutes from the beach.
          </p>
          <p className="text-ink/85 leading-[1.8] text-[17px] mt-5" data-testid="medical-centre-line">
            General practice and pathology are in the same building, and every treatment day has a doctor on site with
            emergency equipment to hand.
          </p>
          {/* Publish Gate 5: the approved co-location sentence is inserted here once the partner approves it. */}
        </Reveal>
      </div>
    </section>

    <section className="bg-warm-white" data-testid="about-standards">
      <div className="shell py-24">
        <Reveal className="text-center mb-14">
          <h2 className="text-deep-sea">Our standards</h2>
          <GoldRule className="mx-auto mt-6" />
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { icon: FileText, title: "Written protocols", text: "Every treatment follows a written protocol — nothing improvised, ever." },
            { icon: ShieldCheck, title: "Screening first", text: "Suitability assessment for every new injectable patient, without exception." },
            { icon: Stethoscope, title: "Complications in-house", text: "Managed in-house, with a doctor available the same day." },
            { icon: BadgeCheck, title: "Registered & published", text: "Every practitioner is AHPRA-registered, with numbers published on their profiles." },
          ].map((item, i) => (
            <Reveal key={item.title} delay={i * 0.08}>
              <div className="card-float p-8 h-full" data-testid={`standard-${i}`}>
                <div className="w-14 h-14 rounded-full border border-gold bg-cream flex items-center justify-center mb-6">
                  <item.icon className="w-6 h-6 text-gold" strokeWidth={1.25} />
                </div>
                <h3 className="text-deep-sea">{item.title}</h3>
                <p className="text-ink/70 text-sm mt-3 leading-relaxed">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-cream" data-testid="about-team">
      <div className="shell py-24">
        <Reveal className="text-center mb-14">
          <h2 className="text-deep-sea">The Lumière Team</h2>
          <GoldRule className="mx-auto mt-6" />
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TEAM.map((member, i) => (
            <PractitionerCard key={member.id} member={member} index={i} />
          ))}
        </div>
      </div>
    </section>

    <CTABand />
  </>
);

export default About;
